import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cv_admin_token";
const STATUSES = ["New", "Contacted", "Demo booked", "Won", "Declined"];
const REVENUES = ["Under $2M", "$2M–$5M", "$5M–$10M", "$10M–$20M", "Over $20M"];

const STATUS_COLOURS = {
  New: "#8b5cf6",
  Contacted: "#3b82f6",
  "Demo booked": "#0ea5e9",
  Won: "#22c55e",
  Declined: "#94a3b8",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function useAuth() {
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/admin/login", { replace: true });
  }, [nav]);
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token || ""}` }), [token]);
  useEffect(() => {
    if (!token) {
      nav("/admin/login", { replace: true });
    }
  }, [token, nav]);
  return { token, authHeader, logout };
}

function SourceBadge({ value }) {
  const v = value || "direct";
  return (
    <span className="admin-source-badge" data-testid="admin-row-source" title={`Source: ${v}`}>{v}</span>
  );
}

function StatusPill({ value, onChange, disabled }) {
  const bg = STATUS_COLOURS[value] || "#8b5cf6";
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="admin-status-pill"
      style={{ background: bg }}
      data-testid="admin-row-status"
    >
      {STATUSES.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  );
}

export default function AdminDashboard() {
  const { token, authHeader, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [revenue, setRevenue] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  const loadList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (status !== "all") params.status = status;
      if (revenue !== "all") params.revenue = revenue;
      const { data } = await axios.get(`${API}/cv-admin/applications`, { params, headers: authHeader });
      setItems(data.items || []);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      setError("Couldn't load applications.");
    } finally {
      setLoading(false);
    }
  }, [authHeader, q, status, revenue, token, logout]);

  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/cv-admin/stats`, { headers: authHeader });
      setStats(data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
    }
  }, [authHeader, token, logout]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const patchRow = async (id, body) => {
    try {
      const { data } = await axios.patch(`${API}/cv-admin/applications/${id}`, body, { headers: authHeader });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      if (selected && selected.id === id) setSelected(data);
      loadStats();
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      alert("Update failed");
    }
  };

  const deleteRow = async (id) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await axios.delete(`${API}/cv-admin/applications/${id}`, { headers: authHeader });
      setItems((prev) => prev.filter((it) => it.id !== id));
      if (selected && selected.id === id) setSelected(null);
      loadStats();
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      alert("Delete failed");
    }
  };

  const exportCsv = async () => {
    const url = new URL(`${API}/cv-admin/applications/export.csv`);
    if (q.trim()) url.searchParams.set("q", q.trim());
    if (status !== "all") url.searchParams.set("status", status);
    if (revenue !== "all") url.searchParams.set("revenue", revenue);
    try {
      const res = await fetch(url.toString(), { headers: authHeader });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `clearview-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      alert("CSV export failed");
    }
  };

  if (!token) return null;

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-top">
        <div className="admin-top-inner">
          <div className="logo"><span className="wm">Clear<span className="wm-accent">view</span>.</span><span className="admin-top-sub"> · Admin</span></div>
          <div className="admin-top-actions">
            <button type="button" className="admin-link-btn" onClick={() => { loadList(); loadStats(); }} data-testid="admin-refresh">Refresh</button>
            <button type="button" className="admin-link-btn" onClick={exportCsv} data-testid="admin-export-csv">Export CSV</button>
            <button type="button" className="admin-link-btn admin-logout" onClick={logout} data-testid="admin-logout">Sign out</button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        <nav className="admin-tabnav" data-testid="admin-tabnav">
          <button
            type="button"
            className="admin-tabnav-btn is-active"
            onClick={() => {}}
            data-testid="admin-tabnav-applications"
          >Applications</button>
          <button
            type="button"
            className="admin-tabnav-btn"
            onClick={() => window.location.assign("/admin/outreach")}
            data-testid="admin-tabnav-outreach"
          >Outreach</button>
        </nav>

        <section className="admin-stats" data-testid="admin-stats">
          <StatCard label="Total" value={stats?.total ?? "—"} testid="admin-stat-total" />
          {STATUSES.map((s) => (
            <StatCard
              key={s}
              label={s}
              value={stats?.[s] ?? "—"}
              colour={STATUS_COLOURS[s]}
              testid={`admin-stat-${s.replace(/\s/g, "-").toLowerCase()}`}
            />
          ))}
        </section>

        {stats && stats.by_source && Object.keys(stats.by_source).length > 0 && (
          <section className="admin-source-breakdown" data-testid="admin-source-breakdown">
            <div className="admin-source-breakdown-label">By source</div>
            <div className="admin-source-breakdown-list">
              {Object.entries(stats.by_source)
                .sort((a, b) => b[1] - a[1])
                .map(([src, n]) => (
                  <span key={src} className="admin-source-breakdown-item" data-testid={`admin-source-${src}`}>
                    <span className="admin-source-breakdown-name">{src}</span>
                    <span className="admin-source-breakdown-count">{n}</span>
                  </span>
                ))}
            </div>
          </section>
        )}

        <section className="admin-filters">
          <input
            className="admin-search"
            type="search"
            placeholder="Search name, business, or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-search"
          />
          <select className="admin-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-filter-status">
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="admin-select" value={revenue} onChange={(e) => setRevenue(e.target.value)} data-testid="admin-filter-revenue">
            <option value="all">All revenue bands</option>
            {REVENUES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </section>

        {error && <div className="admin-error" data-testid="admin-error">{error}</div>}

        <section className="admin-list-wrap">
          <div className="admin-table-card">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Name</th>
                  <th>Business</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Revenue</th>
                  <th>System</th>
                  <th>Deciding between</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={11} className="admin-empty">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={11} className="admin-empty" data-testid="admin-empty">No applications match those filters.</td></tr>
                )}
                {!loading && items.map((it) => (
                  <tr key={it.id} data-testid="admin-row" onClick={() => setSelected(it)}>
                    <td className="admin-when">{fmtDate(it.created_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <StatusPill value={it.status || "New"} onChange={(v) => patchRow(it.id, { status: v })} />
                    </td>
                    <td><SourceBadge value={it.source} /></td>
                    <td className="admin-name">{it.name}</td>
                    <td>{it.business}</td>
                    <td className="admin-email">{it.email}</td>
                    <td>{it.phone || "—"}</td>
                    <td>{it.revenue}</td>
                    <td>{it.system}</td>
                    <td className="admin-decision">{it.decision || "—"}</td>
                    <td className="admin-row-actions" onClick={(e) => e.stopPropagation()}>
                      <a className="admin-row-reply" href={`mailto:${it.email}?subject=${encodeURIComponent("Re: your Clearview demo request")}`}>Reply</a>
                      <button type="button" className="admin-row-delete" onClick={() => deleteRow(it.id)} title="Delete">×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* mobile card list */}
          <div className="admin-cardlist" aria-hidden={false}>
            {loading && <div className="admin-card-empty">Loading…</div>}
            {!loading && items.length === 0 && <div className="admin-card-empty">No applications match those filters.</div>}
            {!loading && items.map((it) => (
              <div key={it.id} className="admin-rowcard" onClick={() => setSelected(it)} data-testid="admin-rowcard">
                <div className="admin-rowcard-top">
                  <div className="admin-rowcard-name">{it.name}<span className="admin-rowcard-biz"> · {it.business}</span></div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <StatusPill value={it.status || "New"} onChange={(v) => patchRow(it.id, { status: v })} />
                  </div>
                </div>
                <div className="admin-rowcard-meta">{fmtDate(it.created_at)} · {it.revenue} · {it.system} · <SourceBadge value={it.source} /></div>
                <div className="admin-rowcard-contact">
                  <a href={`mailto:${it.email}`} onClick={(e) => e.stopPropagation()}>{it.email}</a>
                  {it.phone ? <span> · {it.phone}</span> : null}
                </div>
                {it.decision ? <div className="admin-rowcard-decision">"{it.decision}"</div> : null}
              </div>
            ))}
          </div>
        </section>
      </main>

      {selected && (
        <DetailDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onPatch={(body) => patchRow(selected.id, body)}
          onDelete={() => deleteRow(selected.id)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, colour, testid }) {
  return (
    <div className="admin-stat" data-testid={testid}>
      <div className="admin-stat-label" style={colour ? { color: colour } : undefined}>{label}</div>
      <div className="admin-stat-value">{value}</div>
    </div>
  );
}

function DetailDrawer({ item, onClose, onPatch, onDelete }) {
  const [notes, setNotes] = useState(item.notes || "");
  useEffect(() => { setNotes(item.notes || ""); }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveNotes = () => onPatch({ notes });
  return (
    <div className="admin-drawer-overlay" onClick={onClose} data-testid="admin-drawer">
      <aside className="admin-drawer" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-drawer-close" onClick={onClose} aria-label="Close" data-testid="admin-drawer-close">×</button>
        <div className="admin-drawer-head">
          <div className="admin-drawer-name">{item.name}</div>
          <div className="admin-drawer-biz">{item.business}</div>
          <div className="admin-drawer-meta">{fmtDate(item.created_at)}</div>
        </div>
        <div className="admin-drawer-row"><span>Status</span>
          <StatusPill value={item.status || "New"} onChange={(v) => onPatch({ status: v })} />
        </div>
        <div className="admin-drawer-row"><span>Source</span><SourceBadge value={item.source} /></div>
        <div className="admin-drawer-row"><span>Email</span><a href={`mailto:${item.email}`}>{item.email}</a></div>
        <div className="admin-drawer-row"><span>Phone</span>{item.phone || "—"}</div>
        <div className="admin-drawer-row"><span>Revenue</span>{item.revenue}</div>
        <div className="admin-drawer-row"><span>System</span>{item.system}</div>
        <div className="admin-drawer-block"><div className="admin-drawer-block-label">Deciding between</div><p>{item.decision || "—"}</p></div>
        <div className="admin-drawer-block">
          <div className="admin-drawer-block-label">Call notes</div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            placeholder="Add your notes…"
            data-testid="admin-drawer-notes"
          />
          <div className="admin-drawer-actions">
            <button type="button" className="pill pill-grad admin-drawer-save" onClick={saveNotes} data-testid="admin-drawer-save">Save notes</button>
            <a className="admin-drawer-reply" href={`mailto:${item.email}?subject=${encodeURIComponent("Re: your Clearview demo request")}`}>Reply</a>
            <button type="button" className="admin-drawer-del" onClick={onDelete}>Delete</button>
          </div>
        </div>
      </aside>
    </div>
  );
}
